import { useAnalysisProfileQuery, type AnalysisStatus } from '@verantos-dev/feature-analysis';
import { ASSET_STATUS, type AssetStatus } from '@verantos-dev/types-projects';

import type { EpiResourceRef } from '../types/epi-chat-types';

// Analysis lifecycle → unified asset-status vocabulary. Mirrors
// `analysis_to_asset_status` in the backend (`shared_types.artifact_status`):
// PROCESSED is the terminal-success state, so it reads as Ready.
const ASSET_STATUS_BY_ANALYSIS_STATUS: Record<AnalysisStatus, AssetStatus> = {
  DRAFT: ASSET_STATUS.DRAFT,
  PROCESSING: ASSET_STATUS.PROCESSING,
  PROCESSED: ASSET_STATUS.READY,
  ERROR: ASSET_STATUS.ERROR,
};

/**
 * Analysis status. Reads ``latestStatus`` off the live analysis-profile query
 * (the same cache entry the detail page uses) and maps it to the asset-status
 * label. The query polls itself every 5s while PROCESSING, so the pill flips to
 * Ready/Error live even when the user only watches chat — a run finishes in an
 * async worker with no agent turn. Falls back to Draft while loading (the
 * backend mapper's own safe default).
 */
export const useAnalysisStatusLabel = (ref: EpiResourceRef): string => {
  const { data } = useAnalysisProfileQuery({
    projectId: ref.projectId,
    analysisProfileId: ref.resourceId ?? '',
  });
  const latestStatus = data?.data.latestStatus;
  return (latestStatus && ASSET_STATUS_BY_ANALYSIS_STATUS[latestStatus]) ?? ASSET_STATUS.DRAFT;
};
